import * as React from "react";
import {notifyBugsnagAndShowToast} from "../../../utils/Utils";
import Routes from "../../../navigation/route/Route";
import {none, option, Option, some} from "ts-option";
import Job from "../../../model/job/Job";
import {connect, Dispatch} from "react-redux";
import {OnFailure, OnSuccess, OnSuccessData} from "../../../store/action/actionHelpers";
import {fetchJobByRefNumber} from "../../../store/action/job/job";
import {RootState} from "../../../store/reducer/root/RootState";
import {getJobByIdOrRefNumber} from "../../../store/view/job";
import {RouteComponentProps, withRouter} from "react-router";
import Loading from "../../ui/loading/Loading";
import JobBillOfMaterialsProfits from "./JobBillOfMaterialsProfits";
import Toast from "../../helper/Toast";
import QuoteDetails from "../../../model/quote/QuoteDetails";
import JobBillOfMaterialsHeader from "./JobBillOfMaterialsHeader";
import Price, {CurrencyIsoCode} from "../../../model/common/Price";
import BillOfMaterialsPrices from "./BillOfMaterialsPrices";
import {fetchBillOfMaterialsByJobId, fetchBillsOfMaterialsByJobId} from "../../../store/action/billOfMaterials/billOfMaterials";
import {getBillOfMaterialsByJobId} from "../../../store/view/bom";
import {getInvoiceByJobId} from "../../../store/view/invoice";
import {fetchInvoice} from "../../../store/action/invoice/invoice";
import {getBillByJobId} from "../../../store/view/bill";
import {fetchBillByJobId} from "../../../store/action/bill/bill";
import {getQuoteDetails} from "../../../store/view/quote";
import {fetchQuoteDetails} from "../../../store/action/quote/quote";
import "../../accounting/common/commonDetailsInfoRow.scss";
import {Bom} from "../../../model/job/Bom";
import {BillOfMaterialsPreview} from "../../../model/job/BillOfMaterialsPreview";

interface RouteProps {
	/**
	 * Job reference number.
	 */
	jobReferenceNumber: string;
}

interface ReduxProps {
	/**
	 * Job.
	 */
	job: Option<Job>;
	/**
	 * Bill of Materials for job
	 */
	bomDetails: Option<Bom>;
	/**
	 * Details of quote
	 */
	quoteDetails: Option<QuoteDetails>;
	/**
	 * If true, invoice related to job is in paid stage.
	 */
	isInvoicePaid: boolean;
	/**
	 * If true, bill related to job is in paid stage.
	 */
	isBillPaid: boolean;
	/**
	 * Job profits - bill of materials list
	 */
	jobProfits: Option<BillOfMaterialsPreview[]>;
}

interface State {
	/**
	 * Job fetching in progress
	 */
	jobFetching: boolean;
	/**
	 * Job fetched flag
	 */
	jobFetched: boolean;
	/**
	 * Details of quote fetched flag
	 */
	quoteDetailsFetched: boolean;
	/**
	 * Details of quote fetching flag
	 */
	quoteDetailsFetching: boolean;
}

interface DispatchProps {
	/**
	 * Fetch job
	 * @param {string} jobRefNumber
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	fetchJobByRefNumber: (jobRefNumber: string, onSuccess: OnSuccess, onFailure: OnFailure) => void;
	/**
	 * Fetch Bill Of Materials details
	 * @param {string} jobId
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	fetchBillOfMaterialsByJobId: (jobId: string, onSuccess: OnSuccessData<Bom>, onFailure: OnFailure) => void;
	/**
	 * Fetch invoice action.
	 * @param {string} jobId job identifier.
	 */
	fetchInvoice: (jobId: string) => void;
	/**
	 * Fetch bill action.
	 * @param {string} jobId job identifier.
	 */
	fetchBill: (jobId: string) => void;
	/**
	 * Fetch quote details action.
	 */
	fetchQuoteDetails: (quoteId: string, onSuccess: OnSuccess, onFailure: OnFailure) => void;

	fetchBillsOfMaterialsByJobId: (jobId: string,
		onSuccess: OnSuccessData<BillOfMaterialsPreview[]>,
		onFailure: OnFailure) => void
}

type Props = ReduxProps & DispatchProps & RouteComponentProps<RouteProps>;

class JobBillOfMaterials extends React.PureComponent<Props, State> {

	constructor(props: Props) {
		super(props);
		this.state = {
			jobFetching: false,
			jobFetched: false,
			quoteDetailsFetched: false,
			quoteDetailsFetching: false
		};
	}

	componentDidMount(): void {
		this.props.job.map(j => {
			this.checkJobData(j);
			this.fetchProfits(j.id);
			this.props.fetchInvoice(j.id);
			this.props.fetchBill(j.id);
		});
		this.checkQuoteExistsAndHasItems();
		this.checkJobExists();
	}

	componentWillReceiveProps(nextProps: Props) {
		if (this.props.job.map(j => j.id).orNull !== nextProps.job.map(j => j.id).orNull) {
			nextProps.job.map(j => {
				this.checkJobData(j);
				this.fetchProfits(j.id);
				this.props.fetchInvoice(j.id);
				this.props.fetchBill(j.id);
			});
		}
		if (this.props.job.isDefined && nextProps.job.isEmpty) {
			this.setState({jobFetched: false, jobFetching: false});
		}
		this.checkJobExists(nextProps.job);
		this.checkQuoteExistsAndHasItems(nextProps);
	}

	render() {
		let customerPrice = none, installerEarnings = none;
		if (this.props.bomDetails.isDefined) {
			this.props.bomDetails.map(bom => {
				customerPrice = some(bom.customerPrice);
				installerEarnings = bom.installerEarnings;
			});
		}
		else {
			this.props.quoteDetails.map(quote => {
				customerPrice = option(new Price(quote.totalPrice.cents / 100, CurrencyIsoCode.GBP));
				installerEarnings = option(new Price(quote.getInstallerGross().cents / 100, CurrencyIsoCode.GBP));
			});
		}

		return (
			<div className={"l-page"}>
				<Loading isLoading={this.props.job.isEmpty || this.props.quoteDetails.isEmpty || this.props.jobProfits.isEmpty}>
					{this.props.job.map(j =>
						this.props.jobProfits.map(jp => (
							<>
								<JobBillOfMaterialsHeader job={j}
														  bomId={this.props.bomDetails.map(b => b.id)}
														  onDataRefreshNecessity={this.refreshData}
														  editable={!(this.props.isInvoicePaid || this.props.isBillPaid)}/>
								<div className="c-common-info_row-container">
									<div className="c-common-info_row-inner-container">
										<BillOfMaterialsPrices customerPrice={customerPrice}
															   installerEarnings={installerEarnings}/>
									</div>
								</div>
								<JobBillOfMaterialsProfits job={j}
														   jobProfits={jp}
														   bomDetails={this.props.bomDetails}/>
							</>
						)).orNull
					).orNull}
				</Loading>
			</div>
		);
	}

	checkJobExists(job = this.props.job) {
		if (job.isEmpty) {
			if (this.state.jobFetched) {
				return;
			}
			else if (!this.state.jobFetching) {
				this.setState({jobFetching: true},
					() => this.props.fetchJobByRefNumber(
						this.props.match.params.jobReferenceNumber,
						() => {this.setState({jobFetched: true, jobFetching: false})},
						(err) => {
							notifyBugsnagAndShowToast(err, "Fetch job");
							this.navigateToJobsList();
						}
					)
				);
			}
		}
	}

	/**
	 * If job data is not complete or bom is not allowed for this job, redirects to job details
	 * @param job
	 */
	checkJobData = (job: Job) => {
		if (!job.data.allowBom) {
			this.redirectToJob();
			return
		}
		if (job.quoteId.isEmpty) {
			Toast.error("Quote is required", "Bill of materials");
			this.redirectToJob();
		}
	};

	checkQuoteExistsAndHasItems(props: Props = this.props) {
		if (props.quoteDetails.isEmpty) {
			if (this.state.quoteDetailsFetched) {
				return
			}
			else if (!this.state.quoteDetailsFetching) {
				props.job.map(j => j.quoteId.map(qId => {
					this.setState({quoteDetailsFetching: true});
					props.fetchQuoteDetails(qId, () => this.setState({quoteDetailsFetched: true, quoteDetailsFetching: false}),
						(error) => {
							notifyBugsnagAndShowToast(error, "Cannot fetch quote details", "Bill of materials");
							this.redirectToJob();
						})
				}));
			}
		}
		else {
			if (props.quoteDetails.get.hasNoAssets()) {
				Toast.error("Quote should include assets", "Bill of materials");
				this.redirectToJob();
			}
		}
	}

	fetchProfits = (jobId: string) => {
		this.props.fetchBillsOfMaterialsByJobId(jobId, () => {}, error => {
			notifyBugsnagAndShowToast(error, "Job profits", "Cannot fetch job order profits");
		})
		this.props.fetchBillOfMaterialsByJobId(jobId, () => {}, () => {})
	};

	refreshData = () => {
		this.props.job.map(j => {
			this.props.fetchJobByRefNumber(
				this.props.match.params.jobReferenceNumber,
				() => { this.fetchProfits(j.id); },
				(err) => {
					notifyBugsnagAndShowToast(err, "Fetch job");
					this.navigateToJobsList();
				}
			);
		});
	};

	redirectToJob = () => this.props.history.push(Routes.EditJob(this.props.match.params.jobReferenceNumber));

	navigateToJobsList() {
		this.props.history.push(Routes.JobsList);
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		fetchJobByRefNumber: (jobRefNumber: string, onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(
			fetchJobByRefNumber(jobRefNumber, onSuccess, onFailure)),
		fetchBillOfMaterialsByJobId: (jobId: string, onSuccess: OnSuccessData<Bom>, onFailure: OnFailure) => dispatch(
			fetchBillOfMaterialsByJobId(jobId, onSuccess, onFailure)),
		fetchBillsOfMaterialsByJobId: (jobId: string,
			onSuccess: OnSuccessData<BillOfMaterialsPreview[]>,
			onFailure: OnFailure) => dispatch(
			fetchBillsOfMaterialsByJobId(jobId, onSuccess, onFailure)),
		fetchInvoice: (jobId: string) => dispatch(
			fetchInvoice(jobId, () => {}, () => {})),
		fetchBill: (jobId: string) => dispatch(
			fetchBillByJobId(jobId, () => {}, () => {})),
		fetchQuoteDetails: (quoteId: string, onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(
			fetchQuoteDetails(quoteId, onSuccess, onFailure))
	}
}

function mapStateToProps(state: RootState, props: Props) {
	const job = getJobByIdOrRefNumber(props.match.params.jobReferenceNumber, state);
	const jobId = job.map(j => j.id).orNull;
	const bomDetails = getBillOfMaterialsByJobId(jobId, state);
	const quoteDetails = job.flatMap(j => j.quoteId.flatMap(qId => getQuoteDetails(qId, state)));
	const isInvoicePaid = getInvoiceByJobId(jobId, state).map(inv => inv.isPaid()).getOrElse(false);
	const isBillPaid = getBillByJobId(jobId, state).map(bill => bill.isPaid()).getOrElse(false);
	const jobProfits = option(state.billOfMaterials.bomPreviewMap[jobId])
	return {job, bomDetails, quoteDetails, isInvoicePaid, isBillPaid, jobProfits};
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(JobBillOfMaterials))
